import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { fetchRecommendedModulesByEmployeeId } from "../../api/queries";
import {
  fetchModules,
  fetchMyModuleTaskProgress,
  getModuleDoneCount,
  getModulePage,
  getModuleTaskCount,
  getMyEmployeeId,
} from "../utils/Utils";
import { useI18n } from "../../i18n/I18nProvider";
import type { Module, ActionModule } from "../../types/modules";
import type { ModuleRecommendation, ScoreType } from "../../types/scores";
import { splitModuleRecommendationsByScore } from "../scores/moduleRecommendations";


function getText(en: string | null | undefined, de: string | null | undefined, lang: "en" | "de") {
  return lang === "de" ? de || en || "" : en || de || "";
}

function completionRatio(module: ActionModule) {
  return module.total === 0 ? 0 : module.doneCount / module.total;
}

const RECOMMENDATION_GROUPS: ScoreType[] = ["PBG", "Ext_PRS", "E_Score"];

function completionPct(module: ActionModule) {
  return Math.round(completionRatio(module) * 100);
}

function imageForModule(module: Module) {
  const order = Number(module.order_index ?? 0);
  if (order >= 1 && order <= 9) return `/action_plan/module${order}.png`;
  return "/action_plan/plan.png";
}

function ModuleGrid(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      {props.children}
    </Box>
  );
}

/** Bordered holder that groups one set of module cards under its own heading. */
function ModuleSection(props: {
  title: string;
  caption: string;
  count: number;
  accent?: boolean;
  children: React.ReactNode;
}) {
  const { title, caption, count, accent = false, children } = props;
  const burgundy = "#7A1B2B";
  const burgundySolid = "#96233A";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: accent ? `1px solid #DEAFB9` : "1px solid #E5E7EB",
        bgcolor: accent ? "#FBEFF0" : "#F9FAFB",
        overflow: "hidden",
        ...(accent
          ? {
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: { xs: 4, md: 5 },
                bgcolor: burgundySolid,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          p: 2,
          pl: accent ? { xs: 2.5, md: 3 } : 2,
          bgcolor: accent ? "linear-gradient(120deg, #F3D8DE 0%, #FBEFF0 62%, #FFFFFF 100%)" : "#FFFFFF",
          borderBottom: accent ? "1px solid #DEAFB9" : "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 900, color: accent ? burgundy : "#111827", fontSize: 17 }}>
            {title}
          </Typography>

          <Chip
            label={count}
            size="small"
            sx={{
              height: 22,
              borderRadius: 999,
              bgcolor: accent ? burgundySolid : "#F3F4F6",
              color: accent ? "#FFFFFF" : "#374151",
              fontWeight: 900,
            }}
          />
        </Stack>

        <Typography variant="body2" sx={{ color: accent ? "#9A5563" : "#6B7280", mt: 0.5 }}>
          {caption}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function PlanStat(props: {
  label: string;
  value: number;
  tone: "dark" | "blue" | "green";
}) {
  const palette = {
    dark: { bg: "#111827", color: "#FFFFFF", border: "#111827" },
    blue: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
    green: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0" },
  }[props.tone];

  return (
    <Box
      sx={{
        minWidth: { xs: "calc(50% - 4px)", sm: 104 },
        flex: { xs: "1 1 auto", sm: "0 0 auto" },
        borderRadius: 2,
        border: `1px solid ${palette.border}`,
        bgcolor: palette.bg,
        px: 1.25,
        py: 1,
      }}
    >
      <Typography sx={{ color: palette.color, fontWeight: 950, fontSize: 20, lineHeight: 1 }}>
        {props.value}
      </Typography>
      <Typography variant="caption" sx={{ color: palette.color, fontWeight: 800 }}>
        {props.label}
      </Typography>
    </Box>
  );
}

/** One row in the module list: title + progress bar only, selectable to drive the preview panel. */
function ModuleListRow(props: {
  module: ActionModule;
  selected: boolean;
  onSelect: (module: ActionModule) => void;
}) {
  const { module, selected, onSelect } = props;
  const isCompleted = module.status === "completed";
  const isInProgress = module.status === "in_progress";
  const progress = completionPct(module);
  const barColor = isCompleted ? "#16A34A" : isInProgress ? "#2563EB" : "#111827";

  return (
    <Box
      onClick={() => onSelect(module)}
      role="button"
      aria-pressed={selected}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        px: 1.5,
        py: 1.1,
        border: "1px solid",
        borderColor: selected ? "#111827" : "transparent",
        bgcolor: selected ? "#F3F4F6" : "transparent",
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": { bgcolor: selected ? "#F3F4F6" : "#F9FAFB" },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography
          noWrap
          sx={{ fontWeight: selected ? 900 : 700, color: "#111827", fontSize: 14 }}
        >
          {module.title}
        </Typography>

        {isCompleted ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16A34A", flexShrink: 0 }} />
        ) : null}
      </Stack>

      <LinearProgress
        aria-hidden="true"
        variant="determinate"
        value={progress}
        sx={{
          mt: 0.7,
          height: 5,
          borderRadius: 999,
          bgcolor: "#E5E7EB",
          "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: barColor },
        }}
      />
    </Box>
  );
}

/** Row height cue used to cap the list at ~4 visible rows before it scrolls. */
const MODULE_ROW_MAX_VISIBLE = 4;
const MODULE_ROW_HEIGHT = 66;

function ModuleRowList(props: {
  modules: ActionModule[];
  selectedId?: string;
  onSelect: (module: ActionModule) => void;
}) {
  const { modules, selectedId, onSelect } = props;
  const scrollable = modules.length > MODULE_ROW_MAX_VISIBLE;

  return (
    <Box
      sx={
        scrollable
          ? {
              maxHeight: MODULE_ROW_MAX_VISIBLE * MODULE_ROW_HEIGHT,
              overflowY: "auto",
              pr: 0.5,
            }
          : undefined
      }
    >
      <Stack spacing={0.5}>
        {modules.map((module) => (
          <ModuleListRow
            key={module.id}
            module={module}
            selected={selectedId === module.id}
            onSelect={onSelect}
          />
        ))}
      </Stack>
    </Box>
  );
}

function wordCount(s: string) {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

/** Short "what's inside" bullets for a module — one sentence per category, not the raw checklist items. */
function getModuleHighlights(module: Module, lang: "en" | "de") {
  return (module.module_task_categories ?? [])
    .map((category) => {
      const objective = getText(category.description_en, category.description_de, lang).trim();
      const title = getText(category.title_en, category.title_de, lang).trim();
      const firstTask = getText(
        category.module_tasks?.[0]?.task_en,
        category.module_tasks?.[0]?.task_de,
        lang
      ).trim();

      // A real objective sentence is preferred, but some categories only have a
      // short label (often one word in German) with no separate objective text —
      // pair it with the first checklist item so the bullet is never that bare.
      if (wordCount(objective) >= 3) return objective;
      if (title && firstTask) return `${title}: ${firstTask}.`;
      if (firstTask) return /[.!?]$/.test(firstTask) ? firstTask : `${firstTask}.`;
      if (objective) return /[.!?]$/.test(objective) ? objective : `${objective}.`;
      return title ? `${title}.` : "";
    })
    .filter((sentence): sentence is string => Boolean(sentence));
}

const PREVIEW_MAX_HIGHLIGHTS = 4;

/** Fixed-size detail panel for the selected module: a large faded pictogram behind the description and a capped preview of what's inside. */
function ModulePreviewCard(props: {
  module: ActionModule;
  lang: "en" | "de";
  onOpen: (module: ActionModule) => void;
  L: (en: string, de: string) => string;
}) {
  const { module, lang, onOpen, L } = props;
  const isCompleted = module.status === "completed";
  const isInProgress = module.status === "in_progress";
  const progress = completionPct(module);
  const hasTasks = module.total > 0;
  const highlights = getModuleHighlights(module.raw, lang);
  const shownHighlights = highlights.slice(0, PREVIEW_MAX_HIGHLIGHTS);
  const remainingHighlights = Math.max(0, highlights.length - shownHighlights.length);

  const tileBorder = isCompleted ? "#86EFAC" : isInProgress ? "#60A5FA" : "#E5E7EB";
  const tileBg = isCompleted ? "#F0FDF4" : isInProgress ? "#EFF6FF" : "#FFFFFF";
  const titleColor = isCompleted ? "#166534" : isInProgress ? "#1D4ED8" : "#111827";
  const bodyColor = isCompleted ? "#15803D" : isInProgress ? "#2563EB" : "#6B7280";
  const progressColor = isCompleted ? "#16A34A" : isInProgress ? "#2563EB" : "#111827";
  const metaColor = isCompleted ? "#15803D" : isInProgress ? "#2563EB" : "#6B7280";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: `2px solid ${tileBorder}`,
        bgcolor: tileBg,
        height: { xs: 640, sm: 660 },
        overflow: "hidden",
        boxShadow: "0 10px 24px rgba(17,24,39,0.04)",
      }}
    >
      <Box
        aria-hidden
        component="img"
        src={module.imageSrc}
        alt=""
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "85%", sm: "70%" },
          height: { xs: "85%", sm: "70%" },
          objectFit: "contain",
          opacity: 0.1,
          zIndex: 0,
          pointerEvents: "none",
        }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }} useFlexGap flexWrap="wrap">
          <Chip
            label={L(`Module ${module.order}`, `Modul ${module.order}`)}
            size="small"
            sx={{
              height: 26,
              borderRadius: 999,
              bgcolor: isCompleted ? "#DCFCE7" : isInProgress ? "#DBEAFE" : "#F3F4F6",
              color: isCompleted ? "#166534" : isInProgress ? "#1D4ED8" : "#374151",
              fontWeight: 900,
            }}
          />

          {module.estimatedMinutes > 0 ? (
            <Chip
              label={L(`${module.estimatedMinutes} min`, `${module.estimatedMinutes} Min.`)}
              size="small"
              sx={{ height: 26, borderRadius: 999, bgcolor: "#F3F4F6", color: "#374151", fontWeight: 900 }}
            />
          ) : null}

          {isInProgress ? (
            <Chip
              icon={<PendingActionsOutlinedIcon />}
              label={L("In progress", "In Arbeit")}
              size="small"
              sx={{
                height: 26,
                borderRadius: 999,
                bgcolor: "#2563EB",
                color: "#FFFFFF",
                fontWeight: 900,
                "& .MuiChip-icon": { color: "#FFFFFF" },
              }}
            />
          ) : null}

          {isCompleted ? (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={L("Completed", "Abgeschlossen")}
              size="small"
              sx={{
                height: 26,
                borderRadius: 999,
                bgcolor: "#16A34A",
                color: "#FFFFFF",
                fontWeight: 900,
                "& .MuiChip-icon": { color: "#FFFFFF" },
              }}
            />
          ) : null}
        </Stack>

        <Typography
          sx={{
            fontWeight: 950,
            color: titleColor,
            fontSize: { xs: 22, sm: 26 },
            lineHeight: 1.15,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {module.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: bodyColor,
            lineHeight: 1.6,
            mt: 1,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {module.shortDescription ||
            L(
              "Open this module to see the checklist and complete your next steps.",
              "Öffne dieses Modul, um die Checkliste zu sehen und deine nächsten Schritte zu erledigen."
            )}
        </Typography>

        <Box sx={{ mt: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
            <Typography variant="caption" sx={{ color: metaColor, fontWeight: 800 }}>
              {L("Progress", "Fortschritt")}
            </Typography>
            <Typography variant="caption" sx={{ color: titleColor, fontWeight: 900 }}>
              {hasTasks
                ? L(`${module.doneCount}/${module.total} tasks`, `${module.doneCount}/${module.total} Aufgaben`)
                : L("No tasks", "Keine Aufgaben")}
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: isCompleted ? "#BBF7D0" : isInProgress ? "#BFDBFE" : "#E5E7EB",
              "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: progressColor },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, mt: 3 }}>
          {shownHighlights.length > 0 ? (
            <Box
              sx={{
                pt: 2.5,
                borderTop: "1px solid rgba(17,24,39,0.08)",
                height: "100%",
                boxSizing: "border-box",
                overflowY: "auto",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1.25 }}>
                {L("What's inside", "Was dich erwartet")}
              </Typography>

              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {shownHighlights.map((highlight, i) => (
                  <Typography
                    key={i}
                    component="li"
                    variant="body2"
                    sx={{ color: "#374151", lineHeight: 1.7 }}
                  >
                    {highlight}
                  </Typography>
                ))}
              </Box>

              {remainingHighlights > 0 ? (
                <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 700, mt: 1 }}>
                  {L(`+${remainingHighlights} more inside`, `+${remainingHighlights} weitere im Modul`)}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onOpen(module)}
          sx={{
            ...btnPrimarySx,
            mt: 2,
            flexShrink: 0,
            alignSelf: "center",
            borderRadius: 999,
            px: 5,
            py: 1.5,
            fontSize: 16,
            width: { xs: "100%", sm: "auto" },
            minWidth: { sm: 220 },
            ...(isCompleted
              ? { bgcolor: "#16A34A", "&:hover": { bgcolor: "#15803D", boxShadow: "none" } }
              : isInProgress
              ? { bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" } }
              : {}),
          }}
        >
          {L("Open", "Öffnen")}
        </Button>
      </Box>
    </Paper>
  );
}

export default function ActionPlanOverview(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback(
    (en: string, de: string) => (typedLang === "de" ? de : en),
    [typedLang]
  );

  const [modules, setModules] = React.useState<ActionModule[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const employeeId = await getMyEmployeeId();
        const loadRecommendations = async () => {
          try {
            const { data, error } = await fetchRecommendedModulesByEmployeeId(employeeId);
            if (error) throw error;
            return data ?? [];
          } catch {
            return [];
          }
        };

        const [moduleRows, progressMap, recommendationRows] = await Promise.all([
          fetchModules(),
          fetchMyModuleTaskProgress(employeeId),
          // A missing recommendation set must not keep the plan itself from rendering.
          loadRecommendations(),
        ]);
        const recommendations = ((recommendationRows ?? []) as any[]).map(
          (row): ModuleRecommendation => ({
            module_id: row.module_id,
            module_order: Number(row.module_order),
            name_en: row.modules?.name_en ?? `Module ${row.module_order}`,
            name_de: row.modules?.name_de ?? `Modul ${row.module_order}`,
            trigger_reasons: Array.isArray(row.trigger_reasons) ? row.trigger_reasons : [],
            is_completed: false,
          })
        );
        const recommendationsByScore = splitModuleRecommendationsByScore(recommendations);
        const recommendedIds = new Set(recommendations.map((row) => row.module_id));
        const recommendationScoreById = new Map<string, ScoreType>();

        for (const type of RECOMMENDATION_GROUPS) {
          for (const recommendation of recommendationsByScore[type]) {
            recommendationScoreById.set(recommendation.module_id, type);
          }
        }

        const enriched = moduleRows.map((module, idx): ActionModule => {
          const doneCount = getModuleDoneCount(module, progressMap);
          const total = getModuleTaskCount(module);
          const status =
            total === 0 || doneCount === 0
              ? "not_started"
              : doneCount === total
              ? "completed"
              : "in_progress";
          const impact = Number(module.kontrol_score_pct);

          return {
            id: module.id,
            order: Number(module.order_index ?? idx + 1),
            title: getText(module.name_en, module.name_de, typedLang) || "Module",
            estimatedMinutes: Number(module.estimated_minutes ?? 0),
            shortDescription: getText(module.description_en, module.description_de, typedLang),
            imageSrc: imageForModule(module),
            doneCount,
            total,
            status,
            isRecommended: recommendedIds.has(module.id),
            recommendationScoreType: recommendationScoreById.get(module.id) ?? null,
            impactPct: Number.isFinite(impact) && impact > 0 ? impact : null,
            raw: module,
          };
        });

        if (!mounted) return;
        setModules(enriched.sort((a, b) => a.order - b.order));
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? L("Failed to load modules.", "Module konnten nicht geladen werden."));
        setModules([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [typedLang, L]);

  // Recommended modules come from the backend, ordered by impact so the most
  // useful module leads the list.
  const { recommended, other } = React.useMemo(() => {
    const recommended = modules
      .filter((module) => module.isRecommended)
      .sort(
        (a, b) =>
          (b.impactPct ?? 0) - (a.impactPct ?? 0) ||
          completionRatio(a) - completionRatio(b) ||
          a.order - b.order
      );

    return {
      recommended,
      other: modules.filter((module) => !module.isRecommended),
    };
  }, [modules]);

  const completedCount = modules.filter((module) => module.status === "completed").length;
  const inProgressCount = modules.filter((module) => module.status === "in_progress").length;

  const [selectedModuleId, setSelectedModuleId] = React.useState<string | null>(null);
  const selectedModule =
    modules.find((module) => module.id === selectedModuleId) ?? recommended[0] ?? other[0] ?? null;

  const openModule = (module: ActionModule) => {
    const page = getModulePage(module.raw);

    if (!page) {
      setError(L("No page is mapped for this module.", "Für dieses Modul ist keine Seite zugeordnet."));
      return;
    }

    handlePageChange(page);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1360, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                display: "grid",
                placeItems: "center",
                color: "#111827",
                flexShrink: 0,
              }}
            >
              <ChecklistOutlinedIcon />
            </Box>

            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.1,
                }}
              >
                {L("Your Action Plan", "Dein Aktionsplan")}
              </Typography>

              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Concrete preparation measures to improve your readiness.",
                  "Konkrete Maßnahmen, um deine Vorbereitung zu verbessern."
                )}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => handlePageChange("KontrolScore")}
              sx={{
                ...btnOutlineSx,
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: 0, sm: 220 },
              }}
            >
              {L("Back to Readiness Level", "Zurück zum Bereitschaftsgrad")}
            </Button>

            <Button
              variant="outlined"
              startIcon={<RateReviewOutlinedIcon />}
              onClick={() => handlePageChange("ScoreFeedback")}
              sx={{
                ...btnOutlineSx,
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: 0, sm: 180 },
              }}
            >
              {L("Give feedback", "Feedback geben")}
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
            boxShadow: "0 18px 45px rgba(17,24,39,0.06)",
          }}
        >
          <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #E5E7EB",
                bgcolor: "#F9FAFB",
                overflow: "hidden",
                mb: 2,
              }}
            >
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                >
	                  <Box sx={{ minWidth: 0 }}>
	                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>
	                      {L("How to use this plan", "So nutzt du diesen Plan")}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
                      {L(
                        "Pick one module and complete the checklist step by step. Your progress is saved automatically.",
                        "Wähle ein Modul und erledige die Checkliste Schritt für Schritt. Dein Fortschritt wird automatisch gespeichert."
                      )}
	                    </Typography>
	                  </Box>

                  <Stack
                    direction={{ xs: "row", sm: "row" }}
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ flexShrink: 0 }}
                  >
                    <PlanStat
                      label={L("Recommended", "Empfohlen")}
                      value={recommended.length}
                      tone="dark"
                    />
                    <PlanStat
                      label={L("In progress", "In Arbeit")}
                      value={inProgressCount}
                      tone="blue"
                    />
                    <PlanStat
                      label={L("Done", "Fertig")}
                      value={completedCount}
                      tone="green"
                    />
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            {loading ? (
              <ModuleGrid>
                {[0, 1, 2, 3].map((i) => (
                  <Paper
                    key={i}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      bgcolor: "#FFFFFF",
                      minHeight: 165,
                      p: 2,
                    }}
                  >
                    <Skeleton variant="rounded" height={18} width={120} />
                    <Skeleton variant="rounded" height={22} width="60%" sx={{ mt: 1 }} />
                    <Skeleton variant="rounded" height={16} width="80%" sx={{ mt: 1 }} />
                    <Skeleton variant="rounded" height={36} width={140} sx={{ mt: 2 }} />
                  </Paper>
                ))}
              </ModuleGrid>
            ) : modules.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                  bgcolor: "#FFFFFF",
                  p: 2,
                }}
              >
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {L("No modules found.", "Keine Module gefunden.")}
                </Typography>

                <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
                  {L(
                    "Please add active modules in Supabase.",
                    "Bitte aktive Module in Supabase hinterlegen."
                  )}
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "380px 1fr" },
                  alignItems: "start",
                  gap: 2,
                }}
              >
                <Stack spacing={2}>
                  {recommended.length > 0 ? (
                    <ModuleSection
                      title={L("Recommended for you", "Für dich empfohlen")}
                      caption={L(
                        "Based on your assessment.",
                        "Basierend auf deinem Assessment."
                      )}
                      count={recommended.length}
                      accent
                    >
                      <ModuleRowList
                        modules={recommended}
                        selectedId={selectedModule?.id}
                        onSelect={(m) => setSelectedModuleId(m.id)}
                      />
                    </ModuleSection>
                  ) : null}

                  {other.length > 0 ? (
                    <ModuleSection
                      title={
                        recommended.length > 0
                          ? L("All other modules", "Alle weiteren Module")
                          : L("All modules", "Alle Module")
                      }
                      caption={L(
                        "Keep building your readiness.",
                        "Baue deine Bereitschaft weiter aus."
                      )}
                      count={other.length}
                    >
                      <ModuleRowList
                        modules={other}
                        selectedId={selectedModule?.id}
                        onSelect={(m) => setSelectedModuleId(m.id)}
                      />
                    </ModuleSection>
                  ) : null}
                </Stack>

                <Box sx={{ position: { md: "sticky" }, top: { md: 12 }, maxWidth: 860, mx: { xs: "auto", md: 0 } }}>
                  {selectedModule ? (
                    <ModulePreviewCard module={selectedModule} lang={typedLang} onOpen={openModule} L={L} />
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: "1px dashed #D1D5DB",
                        bgcolor: "#FFFFFF",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ color: "#6B7280" }}>
                        {L("Select a module to preview it.", "Wähle ein Modul, um es anzusehen.")}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const btnPrimarySx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: "#111827",
  boxShadow: "none",
  px: 2.2,
  py: 1.1,
  "&:hover": { bgcolor: "#0B1220", boxShadow: "none" },
} as const;

const btnOutlineSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  px: 2.2,
  py: 1.1,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
} as const;
